"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Coloration syntaxique (fond sombre).
const TOKEN_CLASS = {
    kw: "text-fuchsia-400",
    fn: "text-sky-400",
    str: "text-emerald-400",
    num: "text-amber-300",
    var: "text-sky-200",
    op: "text-gray-400",
};

// Les routes que le visiteur peut demander. Chaque route = un bout de code
// (un "handler") que le serveur exécute pour répondre.
const ROUTES = [
    {
        id: "home",
        navKey: "Node_server_nav_home",
        kind: "page",
        tokens: [
            ["app", "var"],
            [".", "op"],
            ["get", "fn"],
            ["(", "op"],
            ['"/"', "str"],
            [", ", "op"],
            ["envoyerAccueil", "fn"],
            [")", "op"],
        ],
    },
    {
        id: "products",
        navKey: "Node_server_nav_products",
        kind: "page",
        tokens: [
            ["app", "var"],
            [".", "op"],
            ["get", "fn"],
            ["(", "op"],
            ['"/produits"', "str"],
            [", ", "op"],
            ["envoyerProduits", "fn"],
            [")", "op"],
        ],
    },
    {
        id: "weather",
        navKey: "Node_server_nav_weather",
        kind: "json",
        tokens: [
            ["app", "var"],
            [".", "op"],
            ["get", "fn"],
            ["(", "op"],
            ['"/api/meteo"', "str"],
            [", ", "op"],
            ["envoyerMeteo", "fn"],
            [")", "op"],
        ],
    },
];

const PATHS = { home: "/", products: "/produits", weather: "/api/meteo" };

function CodeLine({ tokens }) {
    return (
        <code className="font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap break-words">
            {tokens.map(([text, type], i) => (
                <span key={i} className={TOKEN_CLASS[type]}>
                    {text}
                </span>
            ))}
        </code>
    );
}

export default function NodeServer() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [activeId, setActiveId] = useState("home");
    const [phase, setPhase] = useState("done"); // sending -> running -> done
    const [count, setCount] = useState(1);

    const active = ROUTES.find((r) => r.id === activeId);

    function request(id) {
        setActiveId(id);
        setCount((c) => c + 1);
        setPhase(reduce ? "done" : "sending");
    }

    useEffect(() => {
        if (reduce) return undefined;
        if (phase === "sending") {
            const timer = setTimeout(() => setPhase("running"), 480);
            return () => clearTimeout(timer);
        }
        if (phase === "running") {
            const timer = setTimeout(() => setPhase("done"), 560);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [phase, reduce]);

    const busy = phase !== "done";

    return (
        <section id="nodejs-server" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Node_server_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Node_server_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (teinte Node) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-green-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:nodejs-icon"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        server.js
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-green-300">
                            {t("Node_server_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7 grid lg:grid-cols-2 gap-6 lg:gap-8">
                    {/* LE VISITEUR — navigateur */}
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                            <SolarIcon
                                name="global-bold-duotone"
                                className="w-3.5 h-3.5"
                            />
                            {t("Node_server_visitor_label")}
                        </div>

                        <div className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
                            {/* Barre d'adresse */}
                            <div className="relative flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
                                <SolarIcon
                                    name="lock-keyhole-minimalistic-bold"
                                    className="w-3 h-3 text-slate-400 shrink-0"
                                />
                                <span className="text-[12px] font-mono text-slate-500 truncate">
                                    {t("Node_server_addr")}
                                    <span className="text-slate-800">
                                        {PATHS[activeId]}
                                    </span>
                                </span>
                                {busy && (
                                    <motion.span
                                        className="absolute left-0 bottom-0 h-0.5 bg-green-500"
                                        initial={{ width: "0%" }}
                                        animate={{ width: "100%" }}
                                        transition={{ duration: 1, ease: "easeInOut" }}
                                    />
                                )}
                            </div>

                            {/* Liens du site */}
                            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 flex-wrap">
                                {ROUTES.map((r) => (
                                    <button
                                        key={r.id}
                                        type="button"
                                        onClick={() => request(r.id)}
                                        className={`rounded-full px-3 py-1 text-[12px] font-semibold transition active:scale-95 ${
                                            activeId === r.id
                                                ? "bg-green-500 text-white"
                                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                        }`}
                                    >
                                        {t(r.navKey)}
                                    </button>
                                ))}
                            </div>

                            {/* Viewport */}
                            <div className="relative h-[210px] p-4 bg-white">
                                <AnimatePresence mode="wait">
                                    {busy ? (
                                        <motion.div
                                            key="loading"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-slate-400"
                                        >
                                            <SolarIcon
                                                name="refresh-bold-duotone"
                                                className="w-7 h-7 animate-spin text-green-500"
                                            />
                                            <span className="text-[13px] font-geistSans">
                                                {t("Node_server_loading")}
                                            </span>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key={activeId}
                                            initial={
                                                reduce
                                                    ? false
                                                    : { opacity: 0, y: 8 }
                                            }
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="h-full"
                                        >
                                            {activeId === "home" && (
                                                <div className="h-full flex flex-col items-center justify-center text-center px-2">
                                                    <span className="text-3xl mb-2">
                                                        🌿
                                                    </span>
                                                    <h4 className="text-base font-bold text-slate-900 mb-1">
                                                        {t("Node_server_home_title")}
                                                    </h4>
                                                    <p className="text-[13px] text-slate-500 mb-3 leading-snug">
                                                        {t("Node_server_home_text")}
                                                    </p>
                                                    <span className="inline-flex items-center rounded-full bg-green-500 px-4 py-1.5 text-[13px] font-semibold text-white">
                                                        {t("Node_server_home_btn")}
                                                    </span>
                                                </div>
                                            )}

                                            {activeId === "products" && (
                                                <div className="h-full">
                                                    <h4 className="text-sm font-bold text-slate-900 mb-2.5">
                                                        {t("Node_server_products_title")}
                                                    </h4>
                                                    <div className="flex flex-col gap-2">
                                                        {[
                                                            {
                                                                e: "🪴",
                                                                n: "Node_server_prod1_name",
                                                                p: "Node_server_prod1_price",
                                                            },
                                                            {
                                                                e: "🌵",
                                                                n: "Node_server_prod2_name",
                                                                p: "Node_server_prod2_price",
                                                            },
                                                        ].map((prod) => (
                                                            <div
                                                                key={prod.n}
                                                                className="flex items-center gap-3 rounded-xl bg-slate-50 ring-1 ring-slate-100 px-3 py-2"
                                                            >
                                                                <span className="text-xl">
                                                                    {prod.e}
                                                                </span>
                                                                <span className="flex-1 text-[13px] font-semibold text-slate-800">
                                                                    {t(prod.n)}
                                                                </span>
                                                                <span className="text-[13px] font-bold text-green-600">
                                                                    {t(prod.p)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {activeId === "weather" && (
                                                <div className="h-full">
                                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-2">
                                                        <SolarIcon
                                                            name="code-square-bold"
                                                            className="w-3.5 h-3.5"
                                                        />
                                                        {t("Node_server_json_label")}
                                                    </div>
                                                    <pre className="rounded-xl bg-slate-900 px-3.5 py-3 font-mono text-[12.5px] leading-relaxed overflow-x-auto">
                                                        <span className="text-gray-500">
                                                            {"{"}
                                                        </span>
                                                        {"\n  "}
                                                        <span className="text-sky-300">
                                                            "ville"
                                                        </span>
                                                        <span className="text-gray-400">
                                                            :{" "}
                                                        </span>
                                                        <span className="text-emerald-300">
                                                            "Paris"
                                                        </span>
                                                        <span className="text-gray-400">
                                                            ,
                                                        </span>
                                                        {"\n  "}
                                                        <span className="text-sky-300">
                                                            "temperature"
                                                        </span>
                                                        <span className="text-gray-400">
                                                            :{" "}
                                                        </span>
                                                        <span className="text-amber-300">
                                                            21
                                                        </span>
                                                        <span className="text-gray-400">
                                                            ,
                                                        </span>
                                                        {"\n  "}
                                                        <span className="text-sky-300">
                                                            "ciel"
                                                        </span>
                                                        <span className="text-gray-400">
                                                            :{" "}
                                                        </span>
                                                        <span className="text-emerald-300">
                                                            "{t("Node_server_weather_sky")}"
                                                        </span>
                                                        {"\n"}
                                                        <span className="text-gray-500">
                                                            {"}"}
                                                        </span>
                                                    </pre>
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Statut de la requête */}
                        <div className="mt-3 flex items-center gap-2 text-[12.5px] font-geistSans">
                            {phase === "sending" && (
                                <span className="inline-flex items-center gap-1.5 text-sky-300">
                                    <SolarIcon
                                        name="map-arrow-right-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Node_server_phase_sending")}
                                </span>
                            )}
                            {phase === "running" && (
                                <span className="inline-flex items-center gap-1.5 text-green-300">
                                    <SolarIcon
                                        name="cpu-bolt-bold-duotone"
                                        className="w-4 h-4"
                                    />
                                    {t("Node_server_phase_running")}
                                </span>
                            )}
                            {phase === "done" && (
                                <span className="inline-flex items-center gap-1.5 text-emerald-300">
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Node_server_phase_done")}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* LE SERVEUR — code */}
                    <div className="min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500">
                                <SolarIcon
                                    name="server-bold-duotone"
                                    className="w-3.5 h-3.5"
                                />
                                {t("Node_server_code_label")}
                            </div>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] ring-1 ring-white/10 px-2.5 py-1 text-[11px] font-semibold text-gray-300 tabular-nums">
                                <SolarIcon
                                    name="bolt-bold"
                                    className="w-3 h-3 text-green-300"
                                />
                                {t("Node_server_counter", { count })}
                            </span>
                        </div>

                        <div className="flex flex-col gap-2.5">
                            {ROUTES.map((r) => {
                                const isActive = r.id === activeId;
                                const running = isActive && phase === "running";
                                const done = isActive && phase === "done";
                                return (
                                    <div
                                        key={r.id}
                                        className={`rounded-xl ring-1 px-4 py-3 transition-colors duration-300 ${
                                            done
                                                ? "bg-emerald-500/[0.07] ring-emerald-400/30"
                                                : running
                                                  ? "bg-green-500/[0.08] ring-green-400/40"
                                                  : "bg-white/[0.03] ring-white/10"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <CodeLine tokens={r.tokens} />
                                            {running && (
                                                <span className="shrink-0 inline-flex items-center gap-1 text-[10.5px] font-semibold text-green-300">
                                                    <span className="relative flex h-1.5 w-1.5">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400" />
                                                    </span>
                                                    {t("Node_server_status_running")}
                                                </span>
                                            )}
                                            {done && (
                                                <span className="shrink-0 inline-flex items-center gap-1 text-[10.5px] font-semibold text-emerald-300">
                                                    <SolarIcon
                                                        name="check-circle-bold"
                                                        className="w-3.5 h-3.5"
                                                    />
                                                    {t("Node_server_status_done")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-4 py-3">
                            <SolarIcon
                                name="lightbulb-bolt-bold-duotone"
                                className="w-5 h-5 text-green-300 shrink-0 mt-0.5"
                            />
                            <p className="text-[13px] text-gray-300/90 leading-relaxed mb-0">
                                {t("Node_server_hint")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Node_server_caption")}
            </p>
        </section>
    );
}
